import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { rootUiSelectors, rootUiTypes } from 'app-components/flux/root-ui';
import { getLogger } from 'app-components/utils/core';
import { Map as ImmutableMap } from 'immutable';

import { useAppSelector } from 'hooks/redux';

import type { RootModalList, UrlDialogProps } from './types';

const log = getLogger('components/RootModal/useRootModalUrlDialogs');
const ALERT_DIALOG_TYPE = 'DIALOG_ALERT';

const isUrlDialogObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toUrlDialogProps = (value: unknown): UrlDialogProps | null => {
  if (!isUrlDialogObject(value) || typeof value.dialogType !== 'string') {
    return null;
  }

  return {
    ...value,
    createdAt: typeof value.createdAt === 'number' ? value.createdAt : undefined,
    dialogType: value.dialogType,
    id: typeof value.id === 'string' ? value.id : undefined,
  };
};

export const orderRootModalDialogs = (modalList: RootModalList): RootModalList =>
  modalList.sort((first, second) => {
    const alertOrder = Number(first.type === ALERT_DIALOG_TYPE) - Number(second.type === ALERT_DIALOG_TYPE);
    if (alertOrder !== 0) {
      return alertOrder;
    }

    return (first.createdAt ?? 0) - (second.createdAt ?? 0);
  });

export const useRootModalUrlDialogs = (): RootModalList => {
  const modalsSelector = useAppSelector(rootUiSelectors.getActiveModals) as RootModalList;
  const urlDialogFF = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'urlDialog'));
  const [searchParams, setSearchParams] = useSearchParams();

  const searchProps = searchParams.get('dialogs');
  // URL dialogs are encoded so refresh/deep-link flows can recreate root UI
  // entries alongside dialogs already present in Redux.
  const urlDialogs = useMemo<unknown[]>(() => {
    if (!searchProps) return [];

    try {
      const parsed: unknown = JSON.parse(decodeURIComponent(searchProps));
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      log.error('Failed to parse search props for dialogs:', e);
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          newParams.delete('dialogs');
          return newParams;
        },
        { replace: true },
      );
      return [];
    }
  }, [searchProps, setSearchParams]);

  return useMemo(() => {
    let modalList = modalsSelector;
    if (urlDialogs && urlDialogFF) {
      const validDialogs: UrlDialogProps[] = [];
      let hasInvalidDialogs = false;

      urlDialogs.forEach((props, index) => {
        const urlDialogProps = toUrlDialogProps(props);

        if (!urlDialogProps) {
          log.warn('Skipping invalid dialog props:', props);
          hasInvalidDialogs = true;
          return;
        }

        validDialogs.push(urlDialogProps);

        const propsCopy: Record<string, unknown> = { ...urlDialogProps };
        Reflect.deleteProperty(propsCopy, 'dialogType');
        // Prefer the provided id, then createdAt, so URL-backed dialogs stay
        // stable across renders and avoid duplicate root UI entries.
        const stableId =
          urlDialogProps.id ??
          ((urlDialogProps.createdAt && `url-dialog-${urlDialogProps.dialogType}-${urlDialogProps.createdAt}`) ||
            `url-dialog-${urlDialogProps.dialogType}-${index}`);

        modalList = modalList.push(
          rootUiTypes.mkRootUiData({
            type: urlDialogProps.dialogType,
            props: ImmutableMap(propsCopy ?? {}),
            createdAt: urlDialogProps.createdAt,
            id: stableId,
          }),
        );
      });

      if (hasInvalidDialogs) {
        setSearchParams(
          (prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            if (validDialogs.length > 0) {
              newParams.set('dialogs', encodeURIComponent(JSON.stringify(validDialogs)));
            } else {
              newParams.delete('dialogs');
            }
            return newParams;
          },
          { replace: true },
        );
      }
    }
    return orderRootModalDialogs(modalList);
  }, [modalsSelector, urlDialogs, urlDialogFF, setSearchParams]);
};
